import {
  TextField,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  SelectChangeEvent,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  DataGrid,
  GridRowsProp,
  GridColDef,
  useGridApiRef,
} from '@mui/x-data-grid';
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { AppHeader } from '@eng-mono/atlassian/jira/mojira-bugtracker/frontend-feature-header';
import {
  DataGridPaginationFooter,
  CustomPaginationFooter,
} from '@eng-mono/atlassian/jira/mojira-bugtracker/frontend-feature-issue-list-custom-pagination-footer';
import { IssueListFilter } from '@eng-mono/atlassian/jira/mojira-bugtracker/frontend-feature-issue-list-select';
import {
  buildParamJql,
  safeDecode,
  postJQLSearchResponse,
  JsmIssueBase,
  projectPortalMap,
  getKeyByProject,
  getProjectByIssueKey,
  PaginationModel,
} from '@eng-mono/atlassian/jira/mojira-bugtracker/util-issue-list-fe';
import { MojiraBugtrackerBackendPaths } from '@eng-mono/atlassian/jira/mojira-bugtracker/util-resolver';
import { useBackendAdapter } from '@eng-mono/atlassian/shared/frontend-ui-backend-adapter';
import {
  useClickedLink,
  useDocumentTitle,
} from '@eng-mono/atlassian/shared/frontend-ui-contexts';

import { IssueListView } from './issue-list-issue-view/issue-list-issue-view';
import styles from './issue-list.module.scss';

export interface IssueListProps {
  router?: {
    open(url: string): void;
  };
  pageSize: number;
  siteUrl: string;
}

interface UpdatedJQLSearchResponse extends postJQLSearchResponse {
  pagination?: {
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    knownTotalPages: number;
  };
}

export function IssueList({ pageSize, router, siteUrl }: IssueListProps) {
  const { project } = useParams<{ project: string }>();
  const portal = getKeyByProject(project);
  const navigate = useNavigate();
  useDocumentTitle(`Mojira - ${project} Issues`);
  if (!portal) {
    const issueProject = project ? getProjectByIssueKey(project) : null;
    if (issueProject) {
      navigate(`/browse/${issueProject}/issues/${project}`);
    }
  }

  const [searchParams] = useSearchParams();

  const filter = safeDecode(searchParams.get('filter'));
  const label = safeDecode(searchParams.get('label'));
  const fixVersion = safeDecode(searchParams.get('fixVersion'));
  const typeParam = safeDecode(searchParams.get('type'));

  const paramJql = useMemo(
    () => buildParamJql({ filter, label, fixVersion }),
    [filter, label, fixVersion]
  );

  const { invoke } = useBackendAdapter();
  const {
    clickedLinkId,
    setClickedLinkId,
    paginationModel,
    setPaginationModel,
  } = useClickedLink();

  const [isPaginationReady, setIsPaginationReady] = useState(false);

  const rowCountRef = useRef<number>(0);
  const hasNextPageRef = useRef<boolean>(false);
  const knownTotalPagesRef = useRef<number>(0);
  const requestedPageRef = useRef<number>(0);

  const apiRef = useGridApiRef();

  const [advanced, setAdvanced] = useState<boolean>(() => !!paramJql);
  const [search, setSearch] = useState<string>(() => paramJql || '');
  const [currSearch, setCurrSearch] = useState<string>(() => paramJql || '');
  const [currFilter, setCurrFilter] = useState<string>(() => {
    // Default to 'all' if type=all is in URL (e.g., from legacy filter redirect), otherwise 'open'
    return typeParam === 'all' ? 'all' : 'open';
  });
  const [rawIssues, setRawIssues] = useState<JsmIssueBase[]>([]);
  const [currIssueIndex, setCurrIssueIndex] = useState<number>(0);
  const [issues, setIssues] = useState<GridRowsProp | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [showSessionTimeoutAlert, setShowSessionTimeoutAlert] =
    useState<boolean>(false);

  const [sortField, setSortField] = useState<string>('created');
  const [sortAsc, setSortAsc] = useState<boolean>(false);

  const sortFields = [
    { value: 'created', label: 'Created' },
    { value: 'updated', label: 'Updated' },
    { value: 'priority', label: 'Priority' },
    { value: 'status', label: 'Status' },
  ];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const navigateToIssue = (issueKey: string) => {
    const rawIssue = rawIssues.find((i) => i.key === issueKey);

    isMobile
      ? navigate(`/browse/${project}/issues/${issueKey}`, { state: rawIssue })
      : navigate(`${issueKey}`, { state: rawIssue });
  };

  const fetchIssues = useCallback(
    async (paginationModelArg: PaginationModel) => {
      setLoading(true);
      requestedPageRef.current = paginationModelArg.page;

      try {
        const response = await invoke<UpdatedJQLSearchResponse>(
          MojiraBugtrackerBackendPaths.jqlSearchPost,
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            advanced,
            search,
            project,
            isForge: false,
            sortField,
            sortAsc,
            filter: currFilter,
            page: paginationModelArg.page,
            maxResults: paginationModelArg.pageSize,
          }
        );

        if (!response) {
          return;
        }

        if (
          response.pagination &&
          response.pagination.currentPage === 0 &&
          requestedPageRef.current > 0
        ) {
          setShowSessionTimeoutAlert(true);

          setPaginationModel({ ...paginationModelArg, page: 0 });
        }

        setIssues(
          response.issues.map((issue, index) => ({
            id: issue.id,
            index: index,
            key: issue.key,
            summary: issue.fields.summary,
          }))
        );

        setRawIssues(response.issues);

        if (response.pagination) {
          hasNextPageRef.current = response.pagination.hasNextPage;
          knownTotalPagesRef.current = response.pagination.knownTotalPages;

          const estimatedTotal =
            response.pagination.pageSize * response.pagination.knownTotalPages;
          rowCountRef.current = response.total || estimatedTotal || 0;
        } else {
          rowCountRef.current = response.total || 0;
        }

        if (clickedLinkId) {
          const selectedRowIndex = response.issues.findIndex(
            (row) => row.id === clickedLinkId
          );
          if (selectedRowIndex >= 0) {
            setCurrIssueIndex(selectedRowIndex);
          }
        } else {
          setCurrIssueIndex(0);
        }

        setIsPaginationReady(true);
      } finally {
        setLoading(false);
      }
    },
    [
      currFilter,
      search,
      advanced,
      project,
      sortField,
      sortAsc,
      invoke,
      clickedLinkId,
      setPaginationModel,
    ]
  );

  useEffect(() => {
    fetchIssues(paginationModel);
  }, [fetchIssues, paginationModel]);

  useEffect(() => {
    setClickedLinkId('');
    setPaginationModel({ page: 0, pageSize });
    setIsPaginationReady(false);
  }, [search, currFilter, setPaginationModel, setClickedLinkId, pageSize]);

  const handleSortFieldChange = (event: SelectChangeEvent) => {
    const newSortField = event.target.value as string;
    setPaginationModel({ ...paginationModel, page: 0 });
    setSortField(newSortField);
  };

  const handleSortOrderChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setPaginationModel({ ...paginationModel, page: 0 });
    setSortAsc(checked);
  };

  const handleCloseAlert = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowSessionTimeoutAlert(false);
  };

  const rowCount = rowCountRef.current;

  const columns: GridColDef[] = [
    {
      field: 'issue',
      headerName: '',
      flex: 1,
      renderHeader: () => {
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              padding: isMobile ? '8px' : '10px',
              borderBottom: '1px solid #ccc',
            }}
          >
            <IssueListFilter setFilter={setCurrFilter} />
          </div>
        );
      },
      minWidth: isMobile ? 200 : 250,
      renderCell: (params) => {
        const selectedIssue = rawIssues.find(
          (issue) => issue.key === params.row.key
        );

        return (
          <div
            style={{
              padding: '6px 0',
              lineHeight: '1.2',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: 'auto',
              width: '100%',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <img
                src={selectedIssue?.fields.issuetype.iconUrl}
                alt={`${selectedIssue?.fields.issuetype.name} icon`}
                style={{ width: '20px', height: '20px', marginRight: '8px' }}
              />
              <a
                href={`${siteUrl}/servicedesk/customer/portal/${portal}/${params.row.key}`}
                onClick={(e) => {
                  e.preventDefault();
                  if (router) {
                    router.open(
                      `/servicedesk/customer/portal/${portal}/${params.row.key}`
                    );
                  }
                  if (selectedIssue) {
                    setCurrIssueIndex(params.row.index);
                  }
                }}
                style={{
                  textDecoration: 'none',
                  color: 'blue',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }}
              >
                {params.row.key}
              </a>
            </div>
            <p
              style={{
                margin: '2px 0 0 28px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
              }}
            >
              {params.row.summary}
            </p>
          </div>
        );
      },
    },
  ];

  const handlePaginationModelChange = (newModel: PaginationModel) => {
    if (loading) return;

    const currentPage = paginationModel.page;

    if (newModel.page > currentPage) {
      if (newModel.page === currentPage + 1 && hasNextPageRef.current) {
        setClickedLinkId('');
        setPaginationModel(newModel);
      }
    } else if (newModel.page < currentPage) {
      if (newModel.page >= 0) {
        setClickedLinkId('');
        setPaginationModel(newModel);
      }
    } else if (newModel.pageSize !== paginationModel.pageSize) {
      setPaginationModel({ page: 0, pageSize: newModel.pageSize });
    }
  };

  return (
    <div className={styles['issue-list-container']}>
      <AppHeader
        loginDestination={`${siteUrl}/servicedesk/customer/portal/${portal}/${rawIssues[currIssueIndex]?.key}`}
      />
      <Snackbar
        open={showSessionTimeoutAlert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity="info"
          sx={{ width: '100%' }}
        >
          Your pagination session has expired. You've been returned to the first
          page.
        </Alert>
      </Snackbar>
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'flex-start',
            gap: isTablet ? '15%' : isMobile ? 0 : '18%',
            alignItems: 'center',
            padding: isMobile ? '8px' : '10px',
            backgroundColor: '#fff',
            borderBottom: '1px solid #ccc',
          }}
        >
          <TextField
            id="outlined-controlled"
            label={advanced ? 'Enter JQL to filter' : 'Type to filter'}
            value={currSearch}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setCurrSearch(event.target.value);
            }}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter' && !loading) {
                setSearch(currSearch);
              }
            }}
            fullWidth={isMobile}
            style={{ marginBottom: isMobile ? '16px' : 0 }}
            disabled={loading}
          />

          <div className={styles['sort-wrapper']}>
            <FormControlLabel
              control={
                <Switch
                  checked={advanced}
                  name="jqlSearch"
                  onChange={(_event, checked) => {
                    if (!loading) {
                      setAdvanced(checked);
                      if (checked) {
                        setCurrSearch(search || '');
                        setSortField('');
                      } else {
                        setCurrSearch('');
                        setSearch('');
                        setSortField('created');
                      }
                    }
                  }}
                  disabled={loading}
                />
              }
              aria-live="polite"
              label={advanced ? 'JQL' : 'Basic'}
            />

            {!advanced && (
              <>
                <FormControl
                  variant="outlined"
                  style={{ minWidth: 120 }}
                  disabled={loading || advanced}
                >
                  <InputLabel id="sort-field-label">Sort By</InputLabel>
                  <Select
                    labelId="sort-field-label"
                    id="sort-field-select"
                    value={sortField}
                    onChange={handleSortFieldChange}
                    label="Sort By"
                    aria-live="polite"
                  >
                    {sortFields.map((field) => (
                      <MenuItem key={field.value} value={field.value}>
                        {field.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControlLabel
                  style={{
                    marginLeft: isMobile ? '8px' : '-11px',
                    marginRight: isMobile ? 0 : '16px',
                  }}
                  control={
                    <Switch
                      checked={sortAsc}
                      onChange={handleSortOrderChange}
                      name="sortOrder"
                      disabled={loading || advanced}
                    />
                  }
                  label={sortAsc ? 'Ascending' : 'Descending'}
                />
              </>
            )}
          </div>
        </div>

        <div className={styles['main-content']}>
          <aside aria-label="issue-list" className={styles['issue-grid']}>
            <DataGrid
              style={{ flexGrow: 1 }}
              apiRef={apiRef}
              initialState={{
                filter: {
                  filterModel: {
                    items: [],
                    quickFilterValues: [],
                  },
                },
              }}
              pageSizeOptions={[]}
              slots={{
                //@ts-expect-error err
                footer: isMobile ? () => <div></div> : DataGridPaginationFooter,
              }}
              paginationMode="server"
              rowCount={rowCount}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              rows={issues || []}
              disableColumnResize
              disableColumnSorting
              disableColumnMenu
              disableColumnFilter
              getRowHeight={() => 'auto'}
              columns={columns}
              loading={loading}
              slotProps={{
                loadingOverlay: {
                  variant: 'linear-progress',
                  noRowsVariant: 'skeleton',
                },
                footer: {
                  //@ts-expect-error err
                  loading: loading || !isPaginationReady,
                  hasNextPage: hasNextPageRef.current,
                },
              }}
              rowSelectionModel={
                issues && issues[currIssueIndex]
                  ? [issues[currIssueIndex].id]
                  : []
              }
              onRowSelectionModelChange={(newSelection) => {
                if (!loading) {
                  const selectedId = newSelection[0];
                  setClickedLinkId(selectedId?.toString());
                  const selectedRowIndex = issues?.findIndex(
                    (row) => row.id === selectedId
                  );
                  if (selectedRowIndex !== undefined && selectedRowIndex >= 0) {
                    setCurrIssueIndex(selectedRowIndex);
                    if (apiRef.current) {
                      apiRef.current.scrollToIndexes({
                        rowIndex: selectedRowIndex,
                      });
                    }
                  }

                  if (isMobile && selectedId) {
                    const selectedRow = issues?.find(
                      (row) => row.id === selectedId
                    );
                    if (selectedRow?.key) {
                      navigateToIssue(selectedRow.key);
                    }
                  }
                }
              }}
            />

            {isMobile && (
              <CustomPaginationFooter
                currentPage={paginationModel.page}
                totalPages={
                  knownTotalPagesRef.current ||
                  Math.ceil(rowCountRef.current / paginationModel.pageSize)
                }
                loading={loading || !isPaginationReady}
                styleClass={styles['mobile-pagination']}
                onPrevious={() => {
                  if (paginationModel.page > 0 && !loading) {
                    setPaginationModel({
                      ...paginationModel,
                      page: paginationModel.page - 1,
                    });
                  }
                }}
                onNext={() => {
                  if (hasNextPageRef.current && !loading) {
                    setPaginationModel({
                      ...paginationModel,
                      page: paginationModel.page + 1,
                    });
                  }
                }}
                disablePrevious={paginationModel.page === 0 || loading}
                disableNext={!hasNextPageRef.current || loading}
              />
            )}
          </aside>

          <section className={styles['issue-details']}>
            {!loading && !isMobile && rawIssues.length > 0 && (
              <main><IssueListView
                portalName={projectPortalMap[portal as string].portalName}
                portalProject={projectPortalMap[portal as string].project}
                portalId={portal}
                headerIconUrl={
                  projectPortalMap[portal as string].portalIconUrl as string
                }
                issue={rawIssues[currIssueIndex]}
                total={rowCountRef.current}
                placeholder={''}
                paginationDetails={{
                  rowCount: rowCountRef.current,
                  paginationModel: paginationModel,
                  setPaginationModel: setPaginationModel,
                  currIssueIndex: currIssueIndex,
                  setCurrIssueIndex: setCurrIssueIndex,
                  pageSize: pageSize,
                  hasNextPage: hasNextPageRef.current,
                  knownTotalPages: knownTotalPagesRef.current,
                  isPaginationReady: isPaginationReady,
                }}
                loading={loading}
              /></main>
            )}
          </section>
        </div>
    </div>
  );
}
